import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import FullScreenLoading from '../FullScreenLoading/FullScreenLoading';
import { useGetUser } from '../../shared/queries/useGetUser';
import { roles } from '../../shared/types/Roles';
import { useQueryClient } from '@tanstack/react-query';
import { USER_KEY } from '../../utils/constants/queryKeys';
import { DateFormat, UserDateFormat } from 'src/utils/formatters/formatDate';
import { useGetAutomationPendingConfirmation } from 'src/shared/queries/useGetAutomationPendingConfirmation';
import { useIdleStore } from 'src/stores/IdleStore';
import { useIntercomInitializer } from 'src/utils/hooks/useIntercomInitializer';
import { AddressConfirmationType, PendingConfirmation } from 'src/utils/constants/common';
import { federationCommunicationService } from 'src/shared/federation/utils/federationBridge';
import { FederationEvent, type FeatureFlagValues, type AppDynamicsConfigPayload } from 'src/shared/federation';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { logAppDynamicsEvent, clearAppDynamicsIdentity, setAppDynamicsIdentity } from '../../utils/AppDynamics';
import { initInterceptors } from 'src/shared/services/axios';

const calculateTokenLifetime = (exp: number, iat: number) => (exp - iat) * 1000;
const FOUR_MINUTES_IN_MS = 239999;

const DataInitializerWithLoading = ({ children }: { children: ReactNode }) => {
  const ldClient = useLDClient();
  const [isLdUserIdentified, setIsLdUserIdentified] = useState(false);
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setTimeout } = useIdleStore();
  const [isFn, setIsFn] = useState(false);
  const [hasRedirectedToMac, setHasRedirectedToMac] = useState(false);
  const { data: user, isLoading: isLoadingUser } = useGetUser();
  const { data: automationPendingConfirmation, isLoading: isLoadingAutomationPendingConfirmation } =
    useGetAutomationPendingConfirmation(user?.beneId || 0);
  useIntercomInitializer(auth.isAuthenticated, user);
  const TYPES_FOR_HRX = [roles.hrContact, roles.hrLead, roles.manager];
  const ROLES_FOR_INTERNAL = [roles.internal];
  const ROLE_FOR_BENE = roles.beneficiary;
  const fnUserRedirected = (() => {
    if (sessionStorage.getItem('fnUserRedirected') === 'true') return true;

    const isRedirectParam = new URLSearchParams(window.location.search).get('fnUserRedirected') === 'true';
    if (isRedirectParam) {
      sessionStorage.setItem('fnUserRedirected', 'true');
      return true;
    }
    return false;
  })();

  // don't render the application until the user is identified so that the flag values dont switch after
  // .identify is called
  const onLdUserIdentified = () => {
    setIsLdUserIdentified(true);
  };

  useEffect(() => {
    if (user && ldClient) {
      let didCallCallback = false;
      const timeoutId = window.setTimeout(() => {
        if (!didCallCallback) {
          console.error('LaunchDarkly identify timed out. Proceeding with fallback.');
          setIsLdUserIdentified(true);
        }
      }, 5000); // 5 seconds timeout
      try {
        ldClient.identify(
          {
            kind: 'case',
            key: `company:${user.companyId}`,
            companyId: user.companyId,
          },
          undefined,
          () => {
            didCallCallback = true;
            clearTimeout(timeoutId);
            onLdUserIdentified();
          }
        );
      } catch (err) {
        console.error('Error during LaunchDarkly identify:', err);
        clearTimeout(timeoutId);
        setIsLdUserIdentified(true);
      }
    }
  }, [user?.beneId, user?.companyId, ldClient]);

  useEffect(() => {
    if (auth.user?.access_token) {
      // this is configured by company so we'll honor their preferences here
      const newTimeout = calculateTokenLifetime(auth.user.profile.exp, auth.user.profile.iat);
      // it cannot be less than 4 minutes otherwise the 2 min warning won't work correctly
      if (newTimeout > FOUR_MINUTES_IN_MS) setTimeout(newTimeout);

      const destinationUrl = sessionStorage.getItem('destinationUrl');
      if (destinationUrl) {
        sessionStorage.removeItem('destinationUrl');
        navigate(destinationUrl);
      }
    }

    queryClient.invalidateQueries({
      queryKey: [USER_KEY],
    });
  }, [auth.user?.access_token, navigate, queryClient, setTimeout]);

  useEffect(() => {
    // Set AppDynamics identity on login
    if (!isLoadingUser && user) {
      setAppDynamicsIdentity(user);
    }
  }, [user, isLoadingUser]);

  useEffect(() => {
    const accessToken = auth?.user?.access_token;
    const hasExpired = auth?.user?.expired;

    if (accessToken && !hasExpired) {
      // Log a user login event (custom user data will be merged by config)
      logAppDynamicsEvent('UserLogin', { message: 'User logged in' });

      federationCommunicationService.publish(FederationEvent.AUTH_TOKEN_RECEIVED, accessToken);
    } else {
      clearAppDynamicsIdentity();

      logAppDynamicsEvent('UserLogout', { message: 'User logged out' });
      federationCommunicationService.publish(FederationEvent.AUTH_TOKEN_RECEIVED, undefined);
    }

    // Cleanup function
    return () => {
      federationCommunicationService.publish(FederationEvent.AUTH_TOKEN_RECEIVED, undefined);
    };
  }, [auth?.user?.access_token, auth?.user?.expired]);

  // Broadcast feature flags to remote applications
  useEffect(() => {
    if (!ldClient) return;

    // Function to broadcast current flag values
    const broadcastFlags = () => {
      const flagValues: FeatureFlagValues = {
        'fnx-show-backtodocuments': ldClient.variation('fnx-show-backtodocuments'),
        'enable-dynamic-questionnaire': ldClient.variation('enable-dynamic-questionnaire'),
        // Add other flags here as needed
      };

      federationCommunicationService.publish(FederationEvent.FEATURE_FLAGS_UPDATED, flagValues);
    };

    // Initial broadcast
    broadcastFlags();

    // Listen for LaunchDarkly changes and re-broadcast
    const changeHandler = () => {
      broadcastFlags();
    };

    ldClient.on('change', changeHandler);

    // Cleanup
    return () => {
      ldClient.off('change', changeHandler);
    };
  }, [ldClient]);

  // Broadcast AppDynamics config to remote applications
  useEffect(() => {
    if (window.FN_CONFIG?.APPD_CONFIG) {
      const config: AppDynamicsConfigPayload = window.FN_CONFIG.APPD_CONFIG as AppDynamicsConfigPayload;
      federationCommunicationService.publish(FederationEvent.APPD_CONFIG_UPDATED, config);
    }
  }, []);

  const switchRoles = async () => {
    const linkedUserId = user?.linkedUsers?.[0]?.userId;

    try {
      if (linkedUserId) {
        const switchedUser = await auth.signinSilent({ extraTokenParams: { roleSwitch: linkedUserId } });
        // Point axios at the freshly-switched token immediately. Relying on AxiosProvider's own effect
        // (which reacts to auth.user.access_token asynchronously, on a later render) is racy: if the
        // user-profile refetch below fires before that effect runs, it goes out with the OLD bearer
        // token, idaas keeps returning the pre-switch identity, and `user` never actually updates -
        // which is why gating on isFn/invalidateQueries alone wasn't enough.
        if (switchedUser?.access_token) {
          initInterceptors(switchedUser.access_token);
        }
        // Wait for the user profile to be re-fetched with the new (post-switch) access token before
        // unblocking rendering. Without this, pages relying on `user.userId`/`user.beneId` (e.g. the
        // header's profile image and the Cases page) can briefly - or, if the refetch is ever skipped,
        // permanently - render using the stale pre-switch identity, causing a 500 on the profile image
        // call and an empty Cases list.
        await queryClient.invalidateQueries({ queryKey: [USER_KEY] });
      }
    } finally {
      setIsFn(true);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (user) {
        try {
          const userTypes = user?.userType.types;
          const userRole = user?.userType.role;
          const hasLinkedUser = user?.linkedUsers?.length > 0 && user?.linkedUsers?.[0]?.userType === ROLE_FOR_BENE;
          UserDateFormat.set(user?.userSettings?.dateFormat as DateFormat);
          if (userTypes?.some((type) => TYPES_FOR_HRX.includes(type))) {
            if (!hasLinkedUser) {
              queryClient.invalidateQueries({ queryKey: [USER_KEY] });
              window.sessionStorage.clear();
              window.location.href = window.FN_CONFIG.HR_UI_URL;
            } else {
              switchRoles();
            }
          } else if (userRole && ROLES_FOR_INTERNAL.includes(userRole)) {
            queryClient.invalidateQueries({
              queryKey: [USER_KEY],
            });
            window.sessionStorage.clear();
            window.location.href = window.FN_CONFIG.IMS_UI_URL;
          } else {
            setIsFn(true);
          }
        } catch (error) {}
      }
    };

    fetchData();
  }, [user, isLoadingUser, isLoadingAutomationPendingConfirmation]);

  if (isLoadingUser || isLoadingAutomationPendingConfirmation || !isFn || !isLdUserIdentified) {
    return <FullScreenLoading />;
  }

  if (
    isFn &&
    automationPendingConfirmation?.length > 0 &&
    automationPendingConfirmation.some(
      (item: PendingConfirmation) => item.type === AddressConfirmationType.BeneficiaryLandingPage
    ) &&
    !fnUserRedirected &&
    !hasRedirectedToMac
  ) {
    setHasRedirectedToMac(true);
    navigate('/mailing-address-confirmation');
  }

  return <>{children}</>;
};

export default DataInitializerWithLoading;
